import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import type { MovementDirection } from '@produzionejb/types';
import { useStockMovements } from '../../lib/stock-movements-api';

/**
 * Storico mensile movimenti del MPG: replica Drive MPG.IN/OUT (taggato DOC.T/DOC.N).
 * Mostra tutti gli StockMovement del periodo (mese) del MPG corrente, con filtri
 * direction (IN/OUT/ADJ/ALL) e collapsible per non saturare il form.
 *
 * Sorgente dati: GET /api/stock-movements?periodId=X&pageSize=200, ordinato DESC.
 */
export function MpgMonthHistoryCard({ periodId }: { periodId: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<MovementDirection | 'ALL'>('ALL');

  const movsQ = useStockMovements(
    { periodId, pageSize: 200, ...(filter !== 'ALL' && { direction: filter }) },
    !!periodId && expanded,
  );

  const items = useMemo(() => movsQ.data?.items ?? [], [movsQ.data]);

  const totals = useMemo(() => {
    let inG = 0,
      outG = 0,
      adjG = 0;
    for (const m of items) {
      const q = Number(m.qty);
      if (m.direction === 'IN') inG += q;
      else if (m.direction === 'OUT') outG += q;
      else adjG += q;
    }
    return { inG, outG, adjG };
  }, [items]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <button
              type="button"
              className="flex items-center gap-2 hover:text-brand-700"
              onClick={() => setExpanded((v) => !v)}
            >
              <span>{expanded ? '▼' : '▶'}</span>
              {t('mpg.sections.monthHistory')}
              {expanded && movsQ.data && (
                <span className="text-sm font-normal text-slate-500">({movsQ.data.total})</span>
              )}
            </button>
          </CardTitle>
          {expanded && (
            <div className="flex items-center gap-3">
              <select
                className="rounded border border-slate-300 px-2 py-1 text-xs"
                value={filter}
                onChange={(e) => setFilter(e.target.value as MovementDirection | 'ALL')}
              >
                <option value="ALL">{t('mpg.movements.filterAll')}</option>
                <option value="IN">{t('mpg.movements.filterIn')}</option>
                <option value="OUT">{t('mpg.movements.filterOut')}</option>
                <option value="ADJ">ADJ</option>
              </select>
              <span className="text-xs text-slate-600">
                IN: <span className="font-mono text-blue-700">{totals.inG.toFixed(0)}g</span>{' '}
                OUT: <span className="font-mono text-orange-700">{totals.outG.toFixed(0)}g</span>{' '}
                ADJ: <span className="font-mono text-slate-700">{totals.adjG.toFixed(0)}g</span>
              </span>
            </div>
          )}
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0">
          {movsQ.isLoading && (
            <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>
          )}
          {!movsQ.isLoading && items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('mpg.monthHistory.empty')}</p>
          )}
          {items.length > 0 && (
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-3 py-2">{t('mpg.monthHistory.col.date')}</th>
                    <th className="px-3 py-2">{t('mpg.fields.lotto')}</th>
                    <th className="px-3 py-2">{t('mpg.fields.prodotto')}</th>
                    <th className="px-3 py-2">{t('mpg.movements.direction')}</th>
                    <th className="px-3 py-2 text-right">{t('mpg.fields.qty')}</th>
                    <th className="px-3 py-2">{t('mpg.movements.sourceDoc')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className="px-3 py-1 text-xs text-slate-600">
                        {new Date(m.occurredAt).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-3 py-1 font-mono text-xs">{m.lotCode}</td>
                      <td className="px-3 py-1 text-xs">{m.productCode}</td>
                      <td className="px-3 py-1">
                        <span
                          className={
                            m.direction === 'IN'
                              ? 'rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700'
                              : m.direction === 'OUT'
                                ? 'rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700'
                                : 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700'
                          }
                        >
                          {m.direction}
                        </span>
                      </td>
                      <td className="px-3 py-1 text-right font-mono text-xs">
                        {Number(m.qty).toFixed(0)}g
                      </td>
                      <td className="px-3 py-1 font-mono text-xs">
                        {m.documentType ? `${m.documentType} ${m.documentCode ?? '—'}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {movsQ.data && movsQ.data.total > items.length && (
                <p className="p-2 text-xs text-slate-500">
                  {t('mpg.monthHistory.truncated', {
                    shown: items.length,
                    total: movsQ.data.total,
                  })}
                </p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
